import { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import {
    User,
    Coins,
    Settings,
    Download,
    Loader2,
    Video,
    Folder,
    FileText,
    Scissors,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/useAuth';
import { api, Job } from '@/lib/api';
import { mergeFunnelContext, trackEvent } from '@/lib/analytics';
import { clearPreviewMode, hasTrackedPreviewEvent, isPreviewModeActive, markPreviewEvent } from '@/lib/preview-mode';
import { useToast } from '@/hooks/use-toast';
import { Navbar } from '@/components/layout/Navbar';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { isHistoryRestore } from '@/lib/scroll-memory';
import { landOnHash } from '@/lib/hash-landing';
import { VideoGallery } from '../components/media/VideoGallery';
import { useUserVideos } from '../hooks/useUserVideos';
import { MediaItem } from '../types/video';
import type { ChopBoxHandle } from '@/components/chopbox/ChopBox';
import { CHOPBOX_BRAND } from '@/components/chopbox/brand';
import { isAllowedEditUrl, nameFromUrl } from '@/components/chopbox/ui/state/editLink';
import ExportActions from '../components/media/ExportActions';
import { AuthVideo } from '@/lib/authMedia';
import { isFinishedVideoRun } from '@/lib/corrido-media';
import { AnimateStatus, useAnimate } from '@/components/animate/useAnimate';
import CastingCrew from '@/components/facelock/CastingCrew';

// The editor (and its WebGL engine) loads only on /chopshop, so the landing
// page bundle every visitor downloads doesn't carry it.
const ChopBox = lazy(() => import('@/components/chopbox/ChopBox'));

type ChopBoxOpen = Parameters<ChopBoxHandle['open']>[0];

const FREE_SIGNUP_CREDITS = 5;

function prefersReducedMotion(): boolean {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

interface CorridoRun {
    jobId: string;
    type: string;
    status: string;
    run_id: string | null;
    result_url: string | null;
    /** Set by the server when result_url is the delivered video (optional). */
    video?: boolean | null;
    is_video?: boolean | null;
    created_at: string;
}

/** Number of distinct, non-expired jobs in a /api/web/creations payload
 *  (a bare array or { items }). */
function countRecentJobs(payload: unknown): number {
    const items = Array.isArray(payload)
        ? payload
        : (payload && typeof payload === 'object' && 'items' in payload)
            ? (payload as { items?: Job[] }).items
            : [];
    const ids = new Set<string>();
    for (const job of Array.isArray(items) ? (items as Job[]) : []) {
        if (job?.id && String(job.status || '').toLowerCase() !== 'expired') ids.add(job.id);
    }
    return ids.size;
}

/** ADD YOUR EMAIL (2026-09-13). An account the Telegram studio made on a Buy
 *  credits tap has no email of its own -- it signs in here through the
 *  studio's one-time link. This lets its owner add one and a password, so the
 *  website also works the ordinary way. Shown only to those accounts
 *  (/api/auth/me studio_account); it changes nothing else about the account. */
function AddEmailCard({ onSaved }: { onSaved: () => void }) {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [state, setState] = useState<'idle' | 'saving' | 'done' | 'exists' | 'invalid' | 'failed'>('idle');

    async function save(e: React.FormEvent) {
        e.preventDefault();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) || password.length < 6) {
            setState('invalid');
            return;
        }
        setState('saving');
        const r = await api.addEmail(email.trim(), password);
        if (r.success) {
            setState('done');
            onSaved();
            return;
        }
        setState(r.error === 'email_exists' ? 'exists' : r.error === 'invalid_email' || r.error === 'password_too_short' ? 'invalid' : 'failed');
    }

    if (state === 'done') {
        return <p data-testid="add-email-done" className="mt-4 text-sm text-primary">{t('chopshop.addEmailDone')}</p>;
    }
    return (
        <form id="add-email" onSubmit={save} data-testid="add-email" className="mt-4 scroll-mt-24 rounded-lg border border-border p-4">
            <h3 className="font-medium mb-1">{t('chopshop.addEmailTitle')}</h3>
            <p className="text-sm text-muted-foreground mb-3">{t('chopshop.addEmailBody')}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input
                    type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('chopshop.addEmailEmail')} aria-label={t('chopshop.addEmailEmail')}
                    className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
                <input
                    type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('chopshop.addEmailPassword')} aria-label={t('chopshop.addEmailPassword')}
                    className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
                <Button type="submit" disabled={state === 'saving'}>
                    {state === 'saving' ? t('chopshop.addEmailSaving') : t('chopshop.addEmailSave')}
                </Button>
            </div>
            {state === 'exists' && <p className="mt-2 text-sm text-destructive">{t('chopshop.addEmailExists')}</p>}
            {state === 'invalid' && <p className="mt-2 text-sm text-destructive">{t('chopshop.addEmailInvalid')}</p>}
            {state === 'failed' && <p className="mt-2 text-sm text-destructive">{t('chopshop.addEmailFailed')}</p>}
        </form>
    );
}

/** FINISHED corrido music videos, saved straight into the user's gallery.
 *  (Unfinished projects no longer live in the ChopShop — they sit on the main
 *  window's board as background storyboards you can resume.) */
function CorridoGallery() {
    const { t } = useTranslation();
    const [runs, setRuns] = useState<CorridoRun[]>([]);
    const [deleting, setDeleting] = useState<string | null>(null);

    async function remove(run: CorridoRun) {
        if (!window.confirm(t('media.deleteConfirm', { defaultValue: 'Delete this video? This cannot be undone.' }))) return;
        const token = localStorage.getItem('auth_token');
        if (!token) return;
        setDeleting(run.jobId);
        try {
            const r = await fetch(`/api/web/corrido/run/${encodeURIComponent(run.jobId)}`, {
                method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
            });
            if (r.ok) setRuns((prev) => prev.filter((x) => x.jobId !== run.jobId));
        } finally {
            setDeleting(null);
        }
    }

    useEffect(() => {
        const token = localStorage.getItem('auth_token');
        if (!token) return;
        // Only DELIVERED videos belong here. A completed storyboard row also
        // carries a result_url (its first still), and listing it gave a broken
        // player of a PNG with an .mp4 download button.
        fetch('/api/web/corrido/runs', { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => (r.ok ? r.json() : { runs: [] }))
            .then((j) => setRuns((Array.isArray(j.runs) ? j.runs : []).filter(
                (r: CorridoRun) => isFinishedVideoRun(r) && r.type !== 'corrido_clips' && r.type !== 'corrido_storyboard')))
            .catch(() => { /* gallery is best-effort */ });
    }, []);

    if (!runs.length) return null;
    return (
        <div className="mb-6">
            <h3 className="font-medium mb-3 flex items-center gap-2">
                <Video className="w-4 h-4" /> {t('chopshop.finishedVideos')}
            </h3>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {runs.slice(0, 12).map((run) => (
                    <div key={run.jobId} className="rounded-lg bg-muted p-4">
                        <AuthVideo src={`/api/web/corrido/image?path=${encodeURIComponent(run.result_url as string)}`}
                            controls playsInline
                            style={{ aspectRatio: '9 / 16', background: '#000', borderRadius: 10, maxHeight: 380, width: '100%' }} />
                        <div className="text-xs text-muted-foreground mt-2 mb-3">
                            {new Date(run.created_at).toLocaleDateString()} · corrido music video
                        </div>
                        <ExportActions
                            url={`/api/web/corrido/image?path=${encodeURIComponent(run.result_url as string)}`}
                            filename={`corrido-${String(run.run_id || run.jobId).slice(0, 8)}.mp4`}
                            shareText={t('chopshop.corridoShareText')}
                        />
                        <Button variant="outline" size="sm" className="mt-3 w-full text-destructive border-destructive/40"
                            disabled={deleting === run.jobId} onClick={() => remove(run)}>
                            {deleting === run.jobId ? '…' : t('media.delete', { defaultValue: 'Delete' })}
                        </Button>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function ChopShopPage() {
    const { t } = useTranslation();
    const { user, isLoading: authLoading, refreshUser } = useAuth();
    const navigate = useNavigate();
    const { toast } = useToast();
    const location = useLocation();

    const [recentJobsCount, setRecentJobsCount] = useState(0);
    const [credits, setCredits] = useState(0);
    const [showArchivedMedia, setShowArchivedMedia] = useState(false);
    const shouldAutoRefresh = (location.state as { autoRefresh?: boolean })?.autoRefresh;
    const { videos, loading: mediaLoading, error: mediaError, pollState: mediaPollState } = useUserVideos({
        pollIntervalMs: shouldAutoRefresh ? 3000 : 10000,
        includeArchived: showArchivedMedia
    });
    const [locallySavedMedia, setLocallySavedMedia] = useState<MediaItem[]>([]);
    const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
    const [hiddenMediaUrls, setHiddenMediaUrls] = useState<Set<string>>(new Set());
    // A free signup holds 5 credits (SIGNUP_FREE_CREDITS) and the smallest pack is 10,
    // so any balance above that was bought: the free preview is over for that account.
    const boughtCredits = Math.max(credits, user?.credits ?? 0) > FREE_SIGNUP_CREDITS;
    const previewModeActive = !boughtCredits && isPreviewModeActive(user?.id);
    useEffect(() => {
        if (boughtCredits) clearPreviewMode();
    }, [boughtCredits]);
    // The paid "Animate this photo" in ChopBox, once the server says it runs.
    const animate = useAnimate({ signedIn: !!user, onCredits: () => { void refreshUser(); } });
    const chopboxRef = useRef<ChopBoxHandle | null>(null);
    // An Edit tap or ?edit= link can arrive before the lazy editor has loaded;
    // it is held here and handed over the moment the editor mounts.
    const pendingOpen = useRef<ChopBoxOpen | null>(null);
    const attachChopBox = useCallback((handle: ChopBoxHandle | null) => {
        chopboxRef.current = handle;
        if (handle && pendingOpen.current) {
            const request = pendingOpen.current;
            pendingOpen.current = null;
            handle.open(request);
        }
    }, []);
    const openInChopBox = useCallback((request: ChopBoxOpen) => {
        if (chopboxRef.current) chopboxRef.current.open(request);
        else pendingOpen.current = request;
    }, []);
    const editLinkHandled = useRef(false);
    const userId = user?.id;
    const isValidUrl = (value?: string | null) => typeof value === 'string' && value.trim().length > 0;
    const formatDate = (value?: string | null) => {
        if (!value) return 'Unknown date';
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? 'Unknown date' : date.toLocaleString();
    };

    // /chopshop#my-gallery (the editor's "Show me" after a save, its "saved
    // here" link, Animate's link) lands on that part of the page; Back keeps
    // where it was left instead (ScrollManager puts it back). Once per entry:
    // ChopBox leaving full screen pops back onto this same entry and must not
    // send the page to the gallery again.
    useEffect(() => {
        const id = location.hash.replace(/^#/, '');
        if (!id || isHistoryRestore(location)) return undefined;
        return landOnHash(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key, location.hash]);

    // Sync credits when user changes
    useEffect(() => {
        if (user) {
            setCredits(user.credits);
        }
    }, [user]);

    // Credits refresh. Job history and active-processing left the ChopShop on
    // 2026-07-20 (aa8dbaf9), so this is the only thing still polled here; it
    // backs off while /api/web/credits is timing out.
    useEffect(() => {
        if (!userId) return;
        let cancelled = false;
        let timerId: number | null = null;
        let timeoutStreak = 0;
        const baseIntervalMs = shouldAutoRefresh ? 3000 : 5000;
        const refreshCredits = async () => {
            const result = await api.getCredits();
            if (cancelled) return;
            if (result.success && result.data) {
                setCredits(result.data.balance);
                timeoutStreak = 0;
            } else if (result.requestStatus === 'timeout' || result.requestStatus === 'aborted') {
                timeoutStreak += 1;
            }
            const delay = timeoutStreak > 0
                ? Math.min(baseIntervalMs * 2 ** Math.min(timeoutStreak, 3), 30000)
                : baseIntervalMs;
            timerId = window.setTimeout(() => { void refreshCredits(); }, delay);
        };
        void refreshCredits();
        return () => {
            cancelled = true;
            if (timerId !== null) window.clearTimeout(timerId);
        };
    }, [userId, shouldAutoRefresh]);

    // "Recent jobs" count for the account card: fetched once, not polled.
    useEffect(() => {
        if (!userId) return;
        let cancelled = false;
        void api.getJobHistory().then((result) => {
            if (cancelled) return;
            if (result.success && result.data) {
                setRecentJobsCount(countRecentJobs(result.data));
            } else if (result.requestStatus !== 'timeout' && result.requestStatus !== 'aborted' && result.error) {
                console.error('[ChopShop] Error loading job history:', result.error);
                toast({
                    title: t('chopshop.loadErrorTitle'),
                    description: t('chopshop.loadErrorBody'),
                    variant: 'destructive',
                });
            }
        });
        return () => { cancelled = true; };
    }, [userId, toast, t]);

    // /chopshop?edit=<url> (DirectorConsole's "Edit" link) opens that media in
    // ChopBox once. The value is read as a URL and only our own API paths, our
    // own origin and Cloudinary are accepted: a link must never make the page
    // fetch an arbitrary URL. ChopBox mounts only after auth resolves, so this
    // waits for that, then drops `edit` and keeps every other param.
    useEffect(() => {
        if (editLinkHandled.current || authLoading || !userId) return;
        editLinkHandled.current = true;
        const params = new URLSearchParams(location.search);
        const raw = params.get('edit');
        if (!raw) return;
        if (isAllowedEditUrl(raw)) openInChopBox({ url: raw, name: nameFromUrl(raw) });
        params.delete('edit');
        const search = params.toString();
        navigate({ search: search ? `?${search}` : '' }, { replace: true });
    }, [authLoading, userId, location.search, navigate, openInChopBox]);

    useEffect(() => {
        if (!previewModeActive || hasTrackedPreviewEvent('preview_started')) return;
        trackEvent('preview_started', {
            source: 'chopshop_workspace',
            credits,
        });
        markPreviewEvent('preview_started');
    }, [previewModeActive, credits]);
    const mergedMedia = [
        ...locallySavedMedia,
        ...videos.filter((item) => !locallySavedMedia.some((saved) => saved.url === item.url))
    ].filter((item) => !hiddenMediaUrls.has(item.url));
    const visibleGalleryItems = showArchivedMedia
        ? mergedMedia
        : mergedMedia.filter((item) => item.archived !== true && item.mediaAvailable !== false);
    useEffect(() => {
        if (!previewModeActive || visibleGalleryItems.length === 0 || hasTrackedPreviewEvent('preview_completed')) return;
        trackEvent('preview_completed', {
            source: 'chopshop_gallery',
            resultCount: visibleGalleryItems.length,
        });
        markPreviewEvent('preview_completed');
    }, [previewModeActive, visibleGalleryItems.length]);

    const handleDeleteMedia = async (item: MediaItem) => {
        try {
            const response = await api.deleteUserMediaRecord({
                id: item.id,
                url: item.url,
                job_id: item.jobId
            });
            if (!response.success) {
                throw new Error(response.error || 'Delete failed');
            }
            setHiddenMediaUrls((prev) => {
                const next = new Set(prev);
                next.add(item.url);
                return next;
            });
            setLocallySavedMedia((prev) => prev.filter((entry) => entry.id !== item.id));
            if (selectedMedia?.id === item.id) setSelectedMedia(null);
            toast({
                title: t('chopshop.mediaRemovedTitle'),
                description: t('chopshop.mediaRemovedBody')
            });
        } catch (error) {
            toast({
                title: t('chopshop.deleteFailedTitle'),
                description: error instanceof Error ? error.message : t('chopshop.deleteFailedBody'),
                variant: 'destructive'
            });
        }
    };
    // The gallery's Edit button opens the item in ChopBox (the View button still opens the preview).
    const handleEditMedia = (item: MediaItem) => {
        if (item.mediaType !== 'image' && item.mediaType !== 'video') return;
        openInChopBox({ url: item.url, name: item.title || nameFromUrl(item.url), kind: item.mediaType });
        // 'instant', not 'auto': html has scroll-behavior: smooth, which 'auto' would follow under reduced motion too.
        document.getElementById('chopbox')?.scrollIntoView({ block: 'start', behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
    };
    const archivedCount = mergedMedia.filter((item) => item.archived === true || item.mediaAvailable === false).length;
    if (authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="flex flex-col items-center gap-4">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    <p className="text-muted-foreground animate-pulse">{t('chopshop.loading')}</p>
                </div>
            </div>
        );
    }

    if (!user) {
        return null; // ProtectedRoute should handle this
    }
    const handleRegenerateMedia = (item: MediaItem) => {
        navigate('/create', {
            state: {
                jobType: item.mediaType,
                sourceJobId: item.jobId,
                sourceTitle: item.title
            }
        });
    };
    const handlePreviewUpgrade = (source: string) => {
        mergeFunnelContext({
            funnelSource: source,
            funnelEntryPage: 'chopshop',
            funnelOfferType: 'launch_pack',
            funnelOfferName: 'Launch Pack',
            funnelBillingModel: 'one_time',
        });
        trackEvent('preview_upgrade_clicked', { source });
        navigate('/pricing');
    };
    return (
        <div className="min-h-screen bg-background">
            <Navbar />
            <main className="pt-24">
                <div className="container mx-auto p-4">
                    <div className="flex items-center gap-4 mb-6">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                    <FileText className="w-5 h-5 text-primary-foreground" />
                </div>
                <h1 className="text-2xl font-bold">{t('chopshop.title')}</h1>
                    </div>

            {/* Account Info Section */}
            <div className="mb-8 p-4 bg-card rounded-lg">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                    <User className="w-5 h-5" /> {t('chopshop.account')}
                </h2>
                {previewModeActive ? (
                    <div className="mb-4 rounded-lg border border-primary/20 bg-primary/10 p-4">
                        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                            <div>
                                <div className="text-sm font-semibold text-primary">{t('chopshop.previewActive')}</div>
                                <div className="text-sm text-muted-foreground">{t('chopshop.previewBody')}</div>
                            </div>
                            <Button variant="hero" onClick={() => handlePreviewUpgrade('preview_banner')}>
                                {t('chopshop.unlockExport')}
                            </Button>
                        </div>
                    </div>
                ) : null}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-muted rounded-lg">
                        <h3 className="font-medium mb-2">{t('chopshop.credits')}</h3>
                        <p className="text-2xl font-bold flex items-center gap-2">
                            <Coins className="w-6 h-6" /> {credits}
                        </p>
                    </div>
                    <div className="p-4 bg-muted rounded-lg">
                        <h3 className="font-medium mb-2">{t('chopshop.recentJobs')}</h3>
                        <p className="text-2xl font-bold" data-testid="recent-jobs-count">{recentJobsCount}</p>
                    </div>
                    <div className="p-4 bg-muted rounded-lg">
                        <h3 className="font-medium mb-2">{t('chopshop.settings')}</h3>
                        <Button variant="outline" onClick={() => navigate('/credits-history')}>
                            <Settings className="w-4 h-4 mr-2" /> {t('chopshop.manageAccount')}
                        </Button>
                    </div>
                </div>
                {user?.studio_account && <AddEmailCard onSaved={() => { void refreshUser(); }} />}
            </div>

            {/* Your FaceLock crew: <section id="casting-crew">, between Account
                and ChopBox. It renders nothing at all while the feature is off
                (GET /api/web/facelock/avatars answers 404 feature_off). The
                navbar's and the landing page's Avatars entry open
                /chopshop#casting-crew, landed by landOnHash above. */}
            <CastingCrew
                onAddEmail={user?.studio_account ? () => {
                    const form = document.getElementById('add-email');
                    form?.scrollIntoView({ block: 'start', behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
                    form?.querySelector<HTMLInputElement>('input[type="email"]')?.focus({ preventScroll: true });
                } : undefined}
            />

            {/* Unfinished projects live on the MAIN WINDOW board as background
                storyboards (StoryboardCanvas) — the ChopShop is the finished-work
                home: Gallery, ChopBox, Settings, Account. Nothing else. */}

            {/* ChopBox, the photo and video editor. No ancestor of it may get a
                transform, filter, backdrop-filter, contain: paint or will-change:
                transform: its full screen mode is position: fixed. */}
            <section id="chopbox" aria-labelledby="chopbox-title" className="mb-8 scroll-mt-20 rounded-lg bg-card p-4">
                <h2 id="chopbox-title" className="text-xl font-semibold mb-4 flex items-center gap-2">
                    <Scissors className="w-5 h-5" aria-hidden /> <span translate="no">{CHOPBOX_BRAND}</span>
                </h2>
                <Suspense fallback={
                    <div role="status" className="flex min-h-[320px] items-center justify-center rounded-lg bg-background/40">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" aria-hidden />
                        <span className="sr-only">{t('app.loading')}</span>
                    </div>
                }>
                    {/* Never preview-locked (2026-09-29): editing and downloading are
                        free for everyone on /editor, so an account must not lose them. */}
                    <ChopBox
                        ref={attachChopBox}
                        animate={animate.offer}
                        onSaved={(item) => setLocallySavedMedia((prev) => [
                            { id: item.id, url: item.url, thumbnail: item.url, createdAt: item.createdAt, title: item.name, mediaType: item.kind },
                            ...prev.filter((m) => m.url !== item.url),
                        ])}
                    />
                </Suspense>
            </section>

            <AnimateStatus job={animate.job} credits={animate.credits} onDismiss={animate.dismiss} />

            <div id="my-gallery" className="mb-8 scroll-mt-20 p-4 bg-card rounded-lg">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                    <Folder className="w-5 h-5" /> {t('chopshop.mediaGallery')}
                </h2>
                {/* Finished corrido music videos land straight in the gallery,
                    with the full export row (Phase 4 asset management). */}
                <CorridoGallery />
                {archivedCount > 0 ? (
                    <div className="mb-3">
                        <Button variant="outline" size="sm" onClick={() => setShowArchivedMedia((prev) => !prev)}>
                            {showArchivedMedia ? t('chopshop.hideArchived') : t('chopshop.showArchived', { count: archivedCount })}
                        </Button>
                    </div>
                ) : null}
                {mediaLoading ? (
                    <div>{t('chopshop.loadingMedia')}</div>
                ) : mediaError ? (
                    <div className="text-red-500">{mediaError.message}</div>
                ) : visibleGalleryItems.length > 0 ? (
                    <VideoGallery
                        items={visibleGalleryItems}
                        onSelect={setSelectedMedia}
                        onDelete={handleDeleteMedia}
                        onEdit={handleEditMedia}
                        onRegenerate={handleRegenerateMedia}
                        previewLocked={previewModeActive}
                        onUpgrade={() => handlePreviewUpgrade('preview_gallery_card')}
                    />
                ) : (
                    <p className="text-sm text-muted-foreground">{t('chopshop.noMedia')}</p>
                )}
                {mediaPollState === 'waiting' || mediaPollState === 'retrying' ? (
                    <p className="mt-3 text-sm text-muted-foreground">
                        {mediaPollState === 'retrying' ? t('chopshop.retryingMedia') : t('chopshop.waitingMedia')}
                    </p>
                ) : null}
            </div>

            {selectedMedia ? (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div
                        className="absolute inset-0 bg-black/70"
                        onClick={() => setSelectedMedia(null)}
                    />
                    <div className="relative z-10 max-w-4xl w-full mx-4 bg-background rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h3 className="text-lg font-semibold">{selectedMedia.title}</h3>
                                <p className="text-sm text-muted-foreground">{formatDate(selectedMedia.createdAt || null)}</p>
                            </div>
                            <Button variant="outline" onClick={() => setSelectedMedia(null)}>
                                {t('chopshop.close')}
                            </Button>
                        </div>
                        <div className="w-full">
                            {selectedMedia.mediaType === 'video' ? (
                                <div className="relative">
                                    <video src={selectedMedia.url} controls className="w-full rounded-md bg-black" />
                                    {previewModeActive ? (
                                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                                            <div className="rotate-[-18deg] rounded-xl border border-white/30 bg-black/45 px-4 py-2 text-xs font-bold uppercase tracking-[0.35em] text-white shadow-lg">
                                                {t('chopshop.previewWatermark')}
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            ) : selectedMedia.mediaType === 'audio' || selectedMedia.mediaType === 'voice' ? (
                                <audio src={selectedMedia.url} controls className="w-full" />
                            ) : (
                                <div className="relative">
                                    <img src={selectedMedia.url} alt={selectedMedia.title} className="w-full rounded-md object-contain max-h-[70vh]" />
                                    {previewModeActive ? (
                                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                                            <div className="rotate-[-18deg] rounded-xl border border-white/30 bg-black/45 px-4 py-2 text-xs font-bold uppercase tracking-[0.35em] text-white shadow-lg">
                                                {t('chopshop.previewWatermark')}
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            )}
                        </div>
                        {isValidUrl(selectedMedia.url) ? (
                            <div className="mt-4">
                                {previewModeActive ? (
                                    <Button variant="outline" onClick={() => handlePreviewUpgrade('preview_modal_download')}>
                                        <Download className="w-4 h-4 mr-2" /> {t('chopshop.unlockExportShort')}
                                    </Button>
                                ) : (
                                    <ExportActions
                                        url={selectedMedia.url}
                                        filename={`${(selectedMedia.title || t('chopshop.defaultFilename')).replace(/[^\w.-]+/g, '-')}${selectedMedia.mediaType === 'video' ? '.mp4' : ''}`}
                                        shareText={`${selectedMedia.title || t('chopshop.defaultShareText')} — made with FaceShot ChopShop`}
                                    />
                                )}
                            </div>
                        ) : null}
                    </div>
                </div>
            ) : null}

                </div>
            </main>
        </div>
    );
}
